<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import ThumbnailPageMenu from "./ThumbnailPageMenu.vue";

// Renders one low-res canvas per page via pdfjs's own PDFDocumentProxy
// (foliaPdfViewer.pdfDocument) — independent of the opaque FoliaPdfViewer
// bundle's own rendering/virtualization, so no changes to that bundle are
// needed. Thumbnails are lazy-rendered (IntersectionObserver) and cached
// for the life of this component.
const props = defineProps({
  pdfViewer:   { type: Object, required: true },
  currentPage: { type: Number, default: 1 },
  countPages:  { type: Number, default: 0 },
  // { [pageNumber]: 0 | 90 | 180 | 270 } — owned by the parent (Document.vue),
  // which also applies the same rotation to the main viewer's page. This
  // component only reflects it in the thumbnail and asks to change it.
  rotations:   { type: Object, default: () => ({}) },
  // { [pageNumber]: boolean } — absent/true means visible, false means hidden.
  // Same "owned by the parent, this just reflects it" relationship as rotations.
  visibility:  { type: Object, default: () => ({}) },
  // Owner/Editor only. Gates the per-thumbnail "⋮" menu (rotate, hide/unhide) and
  // whether hidden pages appear at all: managers see them dimmed with a "Hidden"
  // indicator and can unhide them; everyone else has hidden pages omitted
  // entirely from the rail, matching what they see in the main view.
  canManagePages: { type: Boolean, default: false },
});

const emit = defineEmits(["rotate", "toggle-visibility"]);

const THUMB_WIDTH = 104; // px — keep in sync with the w-[104px] thumbnail width in the template

const railEl = ref(null);
const pages = reactive([]);
const baseAspectRatio = ref(0.7727); // fallback ~ US letter portrait, corrected after page 1 loads

const canvasEls = new Map();
const renderedPages = new Set();
const renderingPages = new Set();
const appliedRotation = new Map(); // pageNumber -> rotation last used to render its canvas
let observer = null;

function rotationOf(pageNumber) {
  return props.rotations[pageNumber] ?? 0;
}

function visibilityOf(pageNumber) {
  return props.visibility[pageNumber] ?? true;
}

// Non-managers never see a hidden page at all (no dimmed placeholder) — pages
// itself stays the full, stable list so internal bookkeeping (Maps/Sets keyed
// by page number) doesn't need to reindex; this just filters what renders.
const visiblePages = computed(() =>
  props.canManagePages ? pages : pages.filter((p) => visibilityOf(p.number))
);

function aspectRatioFor(pageNumber) {
  const angle = rotationOf(pageNumber);
  return angle === 90 || angle === 270 ? 1 / baseAspectRatio.value : baseAspectRatio.value;
}

function setCanvasRef(pageNumber, el) {
  if (el) {
    canvasEls.set(pageNumber, el);
  } else {
    // Unmounted (e.g. a non-manager's page got hidden): its drawing went with
    // it, so a fresh <canvas> mounted when the page is unhidden must re-render.
    canvasEls.delete(pageNumber);
    renderedPages.delete(pageNumber);
  }
}

function goToPage(pageNumber) {
  props.pdfViewer.page = pageNumber;
}

function onRotateClick(pageNumber, delta) {
  emit("rotate", pageNumber, delta);
}

function onToggleVisibilityClick(pageNumber) {
  emit("toggle-visibility", pageNumber);
}

async function renderThumbnail(pageNumber) {
  if (renderedPages.has(pageNumber) || renderingPages.has(pageNumber)) return;
  renderingPages.add(pageNumber);
  let canvas = null;
  try {
    const page = await props.pdfViewer.pdfDocument.getPage(pageNumber);
    canvas = canvasEls.get(pageNumber);
    if (!canvas) return; // unmounted or scrolled away before this resolved

    const rotation = rotationOf(pageNumber);
    const unscaledViewport = page.getViewport({ scale: 1, rotation });
    const viewport = page.getViewport({ scale: THUMB_WIDTH / unscaledViewport.width, rotation });

    canvas.width  = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    // Thumbnail was unmounted mid-render — don't mark the page rendered for a
    // canvas that's no longer in the DOM.
    if (canvasEls.get(pageNumber) !== canvas) return;
    renderedPages.add(pageNumber);
    appliedRotation.set(pageNumber, rotation);

    const pageObj = pages[pageNumber - 1];
    if (pageObj) pageObj.aspectRatio = viewport.width / viewport.height;
  } catch (err) {
    console.error(`Thumbnail render failed for page ${pageNumber}`, err);
  } finally {
    renderingPages.delete(pageNumber);
    // A new canvas mounted while this render was in flight (hidden then
    // unhidden quickly) — its IntersectionObserver callback was skipped by the
    // renderingPages guard above, so draw it now.
    const current = canvasEls.get(pageNumber);
    if (current && current !== canvas) renderThumbnail(pageNumber);
  }
}

function buildPages() {
  pages.length = 0;
  for (let i = 1; i <= props.countPages; i++) {
    pages.push({ number: i, aspectRatio: aspectRatioFor(i) });
  }
}

async function primeAspectRatio() {
  try {
    const firstPage = await props.pdfViewer.pdfDocument.getPage(1);
    const vp = firstPage.getViewport({ scale: 1 });
    baseAspectRatio.value = vp.width / vp.height;
  } catch {
    // keep fallback
  }
}

function setupObserver() {
  observer?.disconnect();
  if (!railEl.value) return;
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        renderThumbnail(Number(entry.target.dataset.pageNumber));
      });
    },
    { root: railEl.value, rootMargin: "200px 0px", threshold: 0.01 },
  );
  railEl.value.querySelectorAll(".thumb-item").forEach((el) => observer.observe(el));
}

async function rebuild() {
  if (!props.countPages) return;
  buildPages();
  renderedPages.clear();
  appliedRotation.clear();
  await primeAspectRatio();
  await nextTick();
  setupObserver();
}

watch(() => props.countPages, rebuild);

watch(() => props.currentPage, async (pageNumber) => {
  await nextTick();
  railEl.value
    ?.querySelector(`[data-page-number="${pageNumber}"]`)
    ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
});

// Reacts both to the user's own rotate clicks and to rotations arriving
// from the backend on load — either way, props.rotations is the single
// source of truth (owned by the parent), this just mirrors it.
watch(() => ({ ...props.rotations }), (rotations) => {
  Object.entries(rotations).forEach(([key, angle]) => {
    const pageNumber = Number(key);
    if (appliedRotation.get(pageNumber) === angle) return;

    const pageObj = pages[pageNumber - 1];
    if (pageObj) pageObj.aspectRatio = aspectRatioFor(pageNumber);

    if (!canvasEls.has(pageNumber)) {
      // Not rendered yet — record the target rotation now so the eventual
      // first render (triggered by IntersectionObserver) picks it up, and
      // skip forcing a render for an offscreen thumbnail.
      appliedRotation.set(pageNumber, angle);
      return;
    }
    appliedRotation.delete(pageNumber);
    renderedPages.delete(pageNumber);
    renderThumbnail(pageNumber);
  });
}, { deep: true });

// For non-managers, visiblePages' membership can change (a page gets hidden/
// unhidden by an owner/editor elsewhere) — re-observe so a newly-appearing
// thumbnail actually gets rendered by the IntersectionObserver callback below.
watch(visiblePages, async () => {
  if (props.canManagePages) return;
  await nextTick();
  setupObserver();
});

onMounted(rebuild);
onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
  <div
    ref="railEl"
    class="absolute top-4 left-4 w-[140px] max-h-[calc(100%-32px)] overflow-y-auto overflow-x-hidden
      flex flex-col items-center gap-3 py-3 rounded-2xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.15)]
      dark:bg-slate-800 dark:shadow-[0_4px_20px_rgba(0,0,0,0.4)]"
  >
    <!--
      Keep this comment inside the root <div>: a comment above it would make
      the component a multi-root fragment in dev builds, breaking v-show and
      Document.vue's outside-click check on $el.

      Floats over the page (pinned beside the rail-toggle pill in Document.vue,
      top-aligned with it) instead of sitting in flex flow, so opening it never
      pushes/resizes the document. Sized to content, capped at the available
      height so it scrolls internally once there are enough pages to need it.
    -->
    <!-- .thumb-item is the IntersectionObserver hook in setupObserver() -->
    <div
      v-for="p in visiblePages"
      :key="p.number"
      class="thumb-item group flex flex-col items-center gap-1 w-[104px] cursor-pointer select-none"
      :data-page-number="p.number"
      @click="goToPage(p.number)"
    >
      <div
        class="relative w-[104px] overflow-hidden rounded border-2 bg-slate-100 shadow-[0_0_6px_rgba(0,0,0,0.12)]
          transition-colors duration-150 dark:bg-slate-700 dark:shadow-[0_0_6px_rgba(0,0,0,0.4)]
          after:content-[''] after:absolute after:inset-0 after:bg-black/20 after:pointer-events-none
          after:transition-opacity after:duration-150"
        :class="[
          p.number === currentPage
            ? 'border-slate-600 dark:border-slate-300'
            : 'border-transparent group-hover:border-slate-400 dark:group-hover:border-slate-500',
          p.number === currentPage || !visibilityOf(p.number) ? 'after:opacity-100' : 'after:opacity-0',
        ]"
        :style="{ aspectRatio: p.aspectRatio }"
      >
        <canvas class="block w-full h-full" :ref="(el) => setCanvasRef(p.number, el)" />
        <div
          v-if="canManagePages"
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7
            opacity-0 transition-opacity duration-[120ms] group-hover:opacity-100"
        >
          <ThumbnailPageMenu
            :page-number="p.number"
            :page-hidden="!visibilityOf(p.number)"
            @rotate="onRotateClick"
            @toggle-visibility="onToggleVisibilityClick"
          />
        </div>
        <!--
          Centered in the same spot as the "⋮" menu slot — shown by default and
          swapped out for the menu on hover (managers only).
        -->
        <div
          v-if="canManagePages && !visibilityOf(p.number)"
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex pointer-events-none
            drop-shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-opacity duration-[120ms] group-hover:opacity-0
            dark:[&_rect]:fill-slate-700 dark:[&_path]:stroke-slate-300"
          title="Hidden page"
        >
          <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
            <rect x="2" y="2" width="28.3429" height="28.4196" rx="14.1714" fill="white" />
            <rect width="32" height="32" rx="16" fill="white" />
            <path d="M9.1001 8.80005L22.6001 23.65" stroke="#64748B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path
              d="M18.1205 18.7225C17.4581 19.3247 16.5836 19.6391 15.6894 19.5965C14.7953 19.554 13.9546 19.1579 13.3524 18.4956C12.7503 17.8332 12.4359 16.9587 12.4784 16.0645C12.521 15.1704 12.917 14.3297 13.5794 13.7275"
              stroke="#64748B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
            />
            <path
              d="M16.4854 12.9097C17.2029 13.0471 17.8564 13.4136 18.3479 13.9542C18.8393 14.4947 19.1421 15.1802 19.2107 15.9075"
              stroke="#64748B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
            />
            <path
              d="M22.6513 19.6927C24.4907 18.0457 25.2999 16.2249 25.2999 16.2249C25.2999 16.2249 22.5999 10.1499 15.8499 10.1499C15.2653 10.1491 14.6817 10.1965 14.105 10.2917"
              stroke="#64748B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
            />
            <path
              d="M11.2937 11.2131C7.85368 12.9546 6.3999 16.225 6.3999 16.225C6.3999 16.225 9.0999 22.3 15.8499 22.3C17.4315 22.3124 18.9934 21.948 20.4062 21.2369"
              stroke="#64748B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>
      <!--
        <span
          class="text-[11px]"
          :class="p.number === currentPage
            ? 'font-bold text-slate-700 dark:text-slate-200'
            : 'font-medium text-slate-500 dark:text-slate-400'"
        >{{ p.number }}</span>
      -->
    </div>
  </div>
</template>
